import { useState, useEffect } from 'react';

// Material UI
import { styled } from '@mui/material/styles';
import { Grid, Box, Typography, Tabs, Tab, Button } from '@mui/material';
import {
    InputAdornment,
    FormControlLabel,
    FormControl,
    FormHelperText,
    Switch,
} from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { RadioGroup, Radio } from '@mui/material';
import { Alert } from '@mui/material';

// Components
import ReceiptPreview from 'components/ReceiptPreview';
import SponsorImagePicker from 'components/SponsorImagePicker';
import Tooltip from 'components/popups/Tooltip';
import { TextField, TextArea, Dropdown, DatePicker, TimePicker } from 'components/ui/inputs';
import { Section, Header, Content } from 'components/ui/forms';
import { DeleteButton } from 'components/ui/buttons';
import { SubmitButton } from 'components/ui/buttons';
import { CancelButton } from 'components/ui/buttons';

// Utility
import moment from 'moment-timezone';
import { getMomentFromDateAndTime } from 'utility/validation';
import { useAuth } from 'context/auth';
import { useNotification } from 'context/notification';
import { useDatePicker, useTextInput, useNumberInput, useToggle, useRadio } from 'hooks/forms';
import { formatCurrencyInput } from 'utility/numbers';

export default function ProductForm(props) {
    const { auth } = useAuth();
    const isAdmin = auth?.admin === true;

    // Props
    const {
        editMode,
        defaultValues,
        createdAtMsg,
        updatedAtMsg,
        enablePOS,
        enableQuantity,
        enableMultiScan,
        enableSeatPricing,
        enableGroups = false,
        enableSponsor,
        attendeeRegistration,
        labelColors,
        productGroups,
        onCancel,
        onSubmit,
        onDelete,
        loading,
        deletable,
        autoFocus,
        hostId,
        eventId,
    } = props;

    const [tab, setTab] = useState(0); // 0 = online, 1 = in-person

    const [image, setImage] = useState(defaultValues?.image ?? null);

    const [showLabelModal, setShowLabelModal] = useState(false);

    // New: Assigned Seating Pricing
    //      Set multiple prices per category of seats
    const [seatPricing, setSeatPricing] = useState({
        error: null,
        categories:
            defaultValues?.seatPricing?.map((category) => ({
                priceId: category.priceId ?? null,
                key: category.key,
                name: category.name,
                price: category.price,
                enabled: category.enabled,
                error: false,
                helperText: '',
            })) || [],
    });

    // Selected category index for price preview cycling
    const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);

    // Reset selected category index when categories change or current selection is disabled
    useEffect(() => {
        const currentCategory = seatPricing.categories[selectedCategoryIndex];
        if (!currentCategory || !currentCategory.enabled) {
            const firstEnabledIndex = seatPricing.categories.findIndex((c) => c.enabled);
            setSelectedCategoryIndex(firstEnabledIndex >= 0 ? firstEnabledIndex : 0);
        }
    }, [seatPricing.categories, selectedCategoryIndex]);

    // Online Values
    const name = useTextInput({ defaultValue: defaultValues?.name ?? '', maxLength: 150 });
    const price = useNumberInput({ defaultValue: defaultValues?.price ?? '', format: 'currency' });
    const free = useToggle({ defaultValue: defaultValues?.free ?? false });
    const description = useTextInput({
        defaultValue: defaultValues?.description ?? '',
        maxLength: 1000,
    });
    const productGroupId = useTextInput({
        defaultValue: defaultValues?.productGroupId ? String(defaultValues.productGroupId) : '',
    });
    const overrideDates = useToggle({ defaultValue: defaultValues?.overrideDates ?? false });
    const multipleScan = useToggle({ defaultValue: defaultValues?.multipleScan ?? false });
    const unlimitedEntry = useToggle({ defaultValue: defaultValues?.unlimitedEntry ?? false });
    const startDate = useDatePicker({ defaultValue: defaultValues?.startDate ?? null });
    const startTime = useDatePicker({ defaultValue: defaultValues?.startDate ?? null });
    const endDate = useDatePicker({ defaultValue: defaultValues?.endDate ?? null });
    const endTime = useDatePicker({ defaultValue: defaultValues?.endDate ?? null });
    const labelMessage = useTextInput({
        defaultValue: defaultValues?.labelMessage ?? null,
        maxLength: 15,
    });
    const labelColor = useNumberInput({ defaultValue: defaultValues?.labelColor ?? null });
    const saleStartDate = useDatePicker({ defaultValue: defaultValues?.saleStartDate ?? null });
    const saleStartTime = useDatePicker({ defaultValue: defaultValues?.saleStartDate ?? null });
    const saleEndDate = useDatePicker({ defaultValue: defaultValues?.saleEndDate ?? null });
    const saleEndTime = useDatePicker({ defaultValue: defaultValues?.saleEndDate ?? null });
    const isAttendee = useRadio({ defaultValue: String(defaultValues?.isAttendee ?? '') }); // 'true', 'false', ''
    const quantity = useNumberInput({ defaultValue: defaultValues?.quantity ?? null });
    const minOrder = useNumberInput({ defaultValue: defaultValues?.minOrder ?? 1 });
    const maxOrder = useNumberInput({ defaultValue: defaultValues?.maxOrder ?? 8 });
    const audience = useTextInput({ defaultValue: defaultValues?.audience ?? 'visible' });
    const accessCode = useTextInput({
        defaultValue: defaultValues?.accessCode ?? '',
        maxLength: 50,
    });

    // POS Values
    const posVisible = useTextInput({ defaultValue: defaultValues?.posVisible ?? 'hidden' });
    const posName = useTextInput({ defaultValue: defaultValues?.posName ?? '', maxLength: 50 });
    const posPrice = useTextInput({
        defaultValue: defaultValues?.posPrice ?? '',
        format: 'currency',
    });
    const posFree = useToggle({ defaultValue: defaultValues?.posFree ?? false });

    // Hooks
    const { notify } = useNotification();

    function handleSubmit() {
        if (validateFields()) {
            // Convert date objects to datetime strings
            // Start dates are rounded down to the nearest minute; end dates are rounded up to the nearest minute
            const startDateString = getMomentFromDateAndTime(
                startDate.value,
                startTime.value,
            ).format('YYYY-MM-DD HH:mm:00');
            const endDateString = getMomentFromDateAndTime(endDate.value, endTime.value).format(
                'YYYY-MM-DD HH:mm:59',
            );

            const saleStartDateString = getMomentFromDateAndTime(
                saleStartDate.value,
                saleStartTime.value,
            ).format('YYYY-MM-DD HH:mm:00');
            const saleEndDateString = getMomentFromDateAndTime(
                saleEndDate.value,
                saleEndTime.value,
            ).format('YYYY-MM-DD HH:mm:59');

            // *** Legacy Product Price *** //

            let productPrice = null;

            if (enableSeatPricing === false) {
                productPrice = free.checked ? 0 : Number(price.value);
            }

            // *** Seat / Multi Tiered Pricing *** //

            let seatPricingData = null;

            if (enableSeatPricing) {
                seatPricingData = seatPricing.categories.map((category) => ({
                    priceId: category.priceId,
                    key: category.key,
                    price: category.enabled ? Number(category.price) : 0,
                    enabled: category.enabled,
                }));
            }

            const formData = {
                name: name.value.trim(),
                price: productPrice,
                seatPricing: seatPricingData,
                description: description.value.trim() || null,
                productGroupId: productGroupId.value ? Number(productGroupId.value) : null,
                startDate: startDateString,
                endDate: endDateString,
                labelMessage: labelMessage.value || null,
                labelColor: labelColor.value || null,
                saleStartDate: saleStartDateString,
                saleEndDate: saleEndDateString,
                quantity: enableQuantity ? quantity.value : null,
                minOrder: enableQuantity ? minOrder.value : null,
                maxOrder: enableQuantity ? maxOrder.value : null,
                overrideDates: overrideDates.checked,
                multipleScan: enableMultiScan ? multipleScan.checked : false,
                ...(isAdmin && {
                    unlimitedEntry: enableMultiScan ? unlimitedEntry.checked : false,
                }),
                publish: audience.value === 'visible' || audience.value === 'accessCode',
                accessCode: accessCode.value || null, // If access code is not set, it is null in the DB
                posVisible: posVisible.value === 'visible',
                posName: posName.value || null,
                posPrice: (posFree.checked ? 0 : Number(posPrice.value)) || null,
            };

            if (attendeeRegistration) {
                formData.isAttendee = isAttendee.value === 'true';
            }

            // Edit Mode
            if (editMode) {
                if (image?.includes('https://')) {
                    // Preview image hasn't changed. Don't update sponsorImage
                } else {
                    formData.sponsorImage = image;
                }
                // Create Mode
            } else {
                formData.sponsorImage = image;
            }

            onSubmit(formData);
        } else {
            notify.warning('Please make sure every field is filled in correctly');
        }
    }

    // If overrideDates is unchecked, reset the start and end dates to the event's start and end dates
    useEffect(() => {
        if (overrideDates.checked === false) {
            startDate.setValue(defaultValues?.eventStartDate ?? null);
            startTime.setValue(defaultValues?.eventStartDate ?? null);
            endDate.setValue(defaultValues?.eventEndDate ?? null);
            endTime.setValue(defaultValues?.eventEndDate ?? null);
        }
        // eslint-disable-next-line
    }, [overrideDates.checked]);

    // Clear access code when it is turned off
    useEffect(() => {
        if (audience.value !== 'accessCode') {
            accessCode.setValue('');
        }
        // eslint-disable-next-line
    }, [audience.value]);

    function validateFields() {
        let error = false;

        // Check for empty name
        if (name.value.trim() === '') {
            error = true;
            name.setError('Ticket name is required');
        }

        // Check for empty access code when feature is enabled
        if (audience.value === 'accessCode' && accessCode.value.trim() === '') {
            error = true;
            accessCode.setError('Access code is required');
        }

        if (enableSeatPricing) {
            // Make sure at least one seat category is enabled and has a valid price
            if (seatPricing.categories.every((category) => category.enabled === false)) {
                error = true;
                setSeatPricing((sp) => ({
                    ...sp,
                    error: 'At least one seat category is required',
                }));
            } else {
                let updatedSeatPricing = {
                    ...seatPricing,
                    categories: [...seatPricing.categories],
                };
                for (const category of updatedSeatPricing.categories) {
                    if (category.enabled && (isNaN(category.price) || category.price === '')) {
                        error = true;
                        category.error = true;
                        category.helperText = 'This field cannot be empty';
                    }
                }
                setSeatPricing(updatedSeatPricing);
            }
        } else {
            // Check price if product isn't free
            if (
                !free.checked &&
                (isNaN(price.value) || price.value === '' || Number(price.value) === 0)
            ) {
                error = true;
                price.setError('Price is required');
            }
        }

        if (enableQuantity) {
            // Check for empty quantity, minOrder, and maxOrder
            [quantity, minOrder, maxOrder].forEach((field) => {
                if (field.value === '' || isNaN(field.value) || Number(field.value) <= 0) {
                    error = true;
                    field.setError('Invalid number');
                }
            });

            // Check if minimun order is smaller than quantity - only check if quantity and minOrder are valid
            if (
                !isNaN(quantity.value) &&
                quantity.value > 0 &&
                !isNaN(minOrder.value) &&
                minOrder.value > 0
            ) {
                if (Number(minOrder.value) > Number(quantity.value)) {
                    error = true;
                    minOrder.setError("Can't be more than available");
                }
            }

            // Check if minimum order is smaller than number of products - only check if min and max orders are valid
            if (
                !isNaN(minOrder.value) &&
                minOrder.value > 0 &&
                !isNaN(maxOrder.value) &&
                maxOrder.value > 0
            ) {
                if (Number(minOrder.value) > Number(maxOrder.value)) {
                    error = true;
                    maxOrder.setError("Can't be lower than minimum");
                }
            }
        }

        if (attendeeRegistration && isAttendee.value === '') {
            error = true;
            isAttendee.setError('Please select one option');
        }

        // Check for valid scan dates
        let invalidDate = false;
        [startDate, startTime, endDate, endTime].forEach((field) => {
            if (field.value === null || field.value.isValid() === false) {
                error = true;
                invalidDate = true;
                field.setError('Invalid date');
            }
        });

        // Check if scan end date is after scan start date
        if (invalidDate === false) {
            const fullStartDate = getMomentFromDateAndTime(startDate.value, startTime.value);
            const fullEndDate = getMomentFromDateAndTime(endDate.value, endTime.value);
            if (fullStartDate > fullEndDate) {
                error = true;
                endDate.setError('End date must come after the start date');
                endTime.setError('End date must come after the start date');
            }
        }

        // Check for valid sale dates
        let invalidSaleDate = false;
        [saleStartDate, saleStartTime, saleEndDate, saleEndTime].forEach((field) => {
            if (field.value === null || field.value.isValid() === false) {
                error = true;
                invalidDate = true;
                field.setError('Invalid date');
            }
        });

        if (invalidSaleDate === false) {
            const fullStartDate = getMomentFromDateAndTime(
                saleStartDate.value,
                saleStartTime.value,
            );
            const fullEndDate = getMomentFromDateAndTime(saleEndDate.value, saleEndTime.value);
            if (fullStartDate > fullEndDate) {
                error = true;
                saleEndDate.setError('End date must come after the start date');
                saleEndTime.setError('End date must come after the start date');
            }
        }

        // Return true if one or more errors were set to true
        return !error;
    }

    return (
        <Root>
            {enablePOS && (
                <Box display="flex" justifyContent={'center'}>
                    <TabContainerWrapper>
                        <TabContainer>
                            <Tabs
                                variant="fullWidth"
                                value={tab}
                                onChange={(_, value) => setTab(value)}
                            >
                                <TabItem label="Online" />
                                <TabItem label="In-Person" />
                            </Tabs>
                        </TabContainer>
                    </TabContainerWrapper>
                </Box>
            )}

            {tab === 0 && (
                <Grid container spacing={4}>
                    <Grid item xs={12} md={8}>
                        <Grid container spacing={4}>
                            <Grid item xs={12}>
                                <Section>
                                    <Header>About</Header>
                                    {enableSeatPricing ? (
                                        <Typography>
                                            Create a price option buyers can choose after selecting
                                            a seat. Example: Adult, Child, Senior.
                                        </Typography>
                                    ) : (
                                        <Typography>
                                            Provide a concise name and description, helping
                                            attendees understand the purpose and details of the
                                            ticket they're purchasing.
                                        </Typography>
                                    )}
                                    <Content>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    {...name.formProps}
                                                    autoFocus={autoFocus}
                                                    label={
                                                        enableSeatPricing
                                                            ? 'Price Name (required)'
                                                            : 'Ticket Name (required)'
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextArea
                                                    {...description.formProps}
                                                    minRows={5}
                                                    maxRows={5}
                                                    label={
                                                        enableSeatPricing
                                                            ? 'Price Description (optional)'
                                                            : 'Description (optional)'
                                                    }
                                                />
                                            </Grid>
                                            {enableGroups && (
                                                <Grid item xs={12} sm={6}>
                                                    <Dropdown
                                                        {...productGroupId.formProps}
                                                        label="Display Category"
                                                        InputLabelProps={{ shrink: true }}
                                                    >
                                                        <option value="">- No Category -</option>
                                                        {productGroups.map((group) => (
                                                            <option
                                                                key={group.prod_group_id}
                                                                value={group.prod_group_id}
                                                            >
                                                                {group.prod_group_name}
                                                            </option>
                                                        ))}
                                                    </Dropdown>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Content>
                                </Section>
                            </Grid>

                            <Grid item xs={12}>
                                <Section>
                                    <Header>
                                        {enableSeatPricing ? 'Pricing and Availability' : 'Price'}
                                    </Header>
                                    <Typography>
                                        {enableSeatPricing
                                            ? 'Turn on each category you want to sell this ticket for and set a price. Set to 0 to make the ticket free.'
                                            : "Set the cost of your ticket for online purchases. If your event is free to attend, simply toggle the 'Free' switch."}
                                    </Typography>
                                    <Content>
                                        {enableSeatPricing ? (
                                            <SeatPricing
                                                seatPricing={seatPricing}
                                                setSeatPricing={setSeatPricing}
                                            />
                                        ) : (
                                            <Grid container spacing={2}>
                                                <Grid item xs={6} md={4}>
                                                    <TextField
                                                        {...price.formProps}
                                                        label="Online Price"
                                                        disabled={free.checked}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    $
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid
                                                    item
                                                    xs={6}
                                                    md={8}
                                                    style={{
                                                        display: 'flex',
                                                        alignContent: 'center',
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        control={<Switch {...free.formProps} />}
                                                        label="Free"
                                                    />
                                                </Grid>
                                            </Grid>
                                        )}
                                    </Content>
                                </Section>
                            </Grid>

                            {enableQuantity && (
                                <Grid item xs={12}>
                                    <Section>
                                        <Header>Available Quantity</Header>
                                        <Typography>
                                            Specify the maximum number of tickets available for this
                                            ticket type. Additionally, set the minimum and maximum
                                            limits for the number of tickets that can be purchased
                                            per transaction.
                                        </Typography>
                                        <Content>
                                            <Grid container spacing={2}>
                                                <Grid item xs={6} md={4}>
                                                    <TextField
                                                        {...quantity.formProps}
                                                        label="Qty Available"
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={6} md={4}>
                                                            <TextField
                                                                {...minOrder.formProps}
                                                                label="Order Minimum"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6} md={4}>
                                                            <TextField
                                                                {...maxOrder.formProps}
                                                                label="Order Maximum"
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Content>
                                    </Section>
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Section>
                                    <Header>Ticket Visibility</Header>
                                    <Typography gutterBottom>
                                        Select who can view and purchase this ticket online. Choose
                                        'Available to Everyone' for public access, 'Not Available'
                                        to hide the ticket, or 'Access Code Required' to restrict
                                        access to ticket purchasers with a unique code.
                                    </Typography>
                                    <RadioGroup {...audience.formProps} aria-label="audience">
                                        <FormControlLabel
                                            value="visible"
                                            control={<Radio />}
                                            label="Available to Everyone"
                                        />
                                        <FormControlLabel
                                            value="hidden"
                                            control={<Radio />}
                                            label="Not Available to Anyone"
                                        />
                                        <FormControlLabel
                                            value="accessCode"
                                            control={<Radio />}
                                            label="Access Code Required"
                                        />
                                    </RadioGroup>
                                    {audience.value === 'accessCode' && (
                                        <AccessCodeContainer>
                                            <Typography variant="caption">
                                                code has to be entered on the event page to view
                                                this ticket (max 50 characters)
                                            </Typography>
                                            <AccessCodeInput
                                                {...accessCode.formProps}
                                                placeholder="Access Code"
                                            />
                                        </AccessCodeContainer>
                                    )}
                                </Section>
                            </Grid>

                            <Grid item xs={12}>
                                <Section>
                                    <Header>When is this Ticket Available for Purchase</Header>
                                    <Typography>
                                        Define the start and end dates and times during which the
                                        ticket will be available for purchase online.
                                    </Typography>
                                    <Box mt={2}>
                                        <Grid container>
                                            <Grid item xs={6}>
                                                <Box mr={[0, '1rem']} mb={3}>
                                                    <DatePicker
                                                        {...saleStartDate.formProps}
                                                        label="Start Date"
                                                    />
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box ml={[0, '1rem']} mb={3}>
                                                    <TimePicker
                                                        {...saleStartTime.formProps}
                                                        label="Start Time"
                                                    />
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box mr={[0, '1rem']}>
                                                    <DatePicker
                                                        {...saleEndDate.formProps}
                                                        label="End Date"
                                                    />
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box ml={[0, '1rem']}>
                                                    <TimePicker
                                                        {...saleEndTime.formProps}
                                                        label="End Time"
                                                    />
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        <DateSummary
                                            startDate={saleStartDate.value}
                                            startTime={saleStartTime.value}
                                            endDate={saleEndDate.value}
                                            endTime={saleEndTime.value}
                                        />
                                    </Box>
                                </Section>
                            </Grid>

                            <Grid item xs={12}>
                                <Section>
                                    <Header>When can this Ticket be Scanned</Header>
                                    <Typography>
                                        Customize when the ticket will be scanned for admission into
                                        the event. The ticket will be considered invalid outside of
                                        this timeframe.
                                    </Typography>
                                    <Content>
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            mb={2}
                                            alignItems="flex-start"
                                        >
                                            <FormControlLabel
                                                control={<Switch {...overrideDates.formProps} />}
                                                label="Limit Check-in Time"
                                            />
                                            <SwitchSubHeader>
                                                Enable this feature if you'd like to set specific
                                                days and times for when this ticket type can be
                                                scanned for admission at the event.
                                            </SwitchSubHeader>
                                        </Box>

                                        <Box mb={4}>
                                            <Grid container>
                                                <Grid item xs={6}>
                                                    <Box mr={[0, '1rem']} mb={3}>
                                                        <DatePicker
                                                            {...startDate.formProps}
                                                            disabled={!overrideDates.checked}
                                                            label="Start Date"
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box ml={[0, '1rem']} mb={3}>
                                                        <TimePicker
                                                            {...startTime.formProps}
                                                            disabled={!overrideDates.checked}
                                                            label="Start Time"
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box mr={[0, '1rem']}>
                                                        <DatePicker
                                                            {...endDate.formProps}
                                                            disabled={!overrideDates.checked}
                                                            label="End Date"
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Box ml={[0, '1rem']}>
                                                        <TimePicker
                                                            {...endTime.formProps}
                                                            disabled={!overrideDates.checked}
                                                            label="End Time"
                                                        />
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>

                                        {enableMultiScan && (<>
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                mb={2}
                                                alignItems="flex-start"
                                            >
                                                <FormControlLabel
                                                    control={<Switch {...multipleScan.formProps} />}
                                                    label="Allow Multi-day Check-in"
                                                />
                                                <SwitchSubHeader>
                                                    Turn this feature on to allow this ticket to be
                                                    checked-in once per calendar day for the
                                                    duration of your event.
                                                </SwitchSubHeader>
                                            </Box>

                                            {isAdmin && (
                                                <Box
                                                    display="flex"
                                                    flexDirection="column"
                                                    mb={2}
                                                    alignItems="flex-start"
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Switch {...unlimitedEntry.formProps} />
                                                        }
                                                        label="Allow Unlimited Check-in"
                                                    />
                                                    <SwitchSubHeader>
                                                        Allow this ticket to be checked in any number
                                                        of times during its valid check-in window.
                                                    </SwitchSubHeader>
                                                </Box>
                                            )}
                                        </>)}
                                        <ScanDateSummary
                                            startDate={startDate.value}
                                            startTime={startTime.value}
                                            endDate={endDate.value}
                                            endTime={endTime.value}
                                            multiCheckins={multipleScan.checked}
                                            unlimitedCheckins={unlimitedEntry.checked}
                                        />
                                    </Content>
                                </Section>
                            </Grid>

                            <Grid item xs={12}>
                                <Section>
                                    <Header>Check-In Directions</Header>
                                    <Typography>
                                        Show a label when this ticket is scanned in. You can use
                                        this to instruct your volunteers to give a bracelet to the
                                        guest.
                                    </Typography>
                                    <Content>
                                        <LabelPreview
                                            onClick={() => setShowLabelModal(true)}
                                            color={
                                                labelColor.value
                                                    ? labelColors.find(
                                                        (c) =>
                                                            c.color_id ===
                                                            Number(labelColor.value),
                                                    )?.color_hex
                                                    : null
                                            }
                                        >
                                            {labelMessage.value ? (
                                                <LabelMessagePreview
                                                    labelColor={
                                                        labelColor.value
                                                            ? labelColors.find(
                                                                (c) =>
                                                                    c.color_id ===
                                                                    labelColor.value,
                                                            )?.color_hex
                                                            : null
                                                    }
                                                >
                                                    {labelMessage.value}
                                                </LabelMessagePreview>
                                            ) : (
                                                <Typography>+ add a label</Typography>
                                            )}
                                        </LabelPreview>
                                    </Content>
                                </Section>
                            </Grid>

                            {showLabelModal === true && (
                                <LabelModal
                                    labelColors={labelColors}
                                    message={labelMessage.value}
                                    color={labelColor.value}
                                    open={showLabelModal}
                                    onClose={() => setShowLabelModal(false)}
                                    onSave={(message, color) => {
                                        setShowLabelModal(false);
                                        labelMessage.setValue(message);
                                        labelColor.setValue(color);
                                    }}
                                />
                            )}

                            <Grid item xs={12}>
                                <Section>
                                    <Header>Ticket Sponsorship Image</Header>
                                    <Typography>
                                        Upload an image to appear in the bottom left corner of the
                                        PDF version of this ticket, which can be used to showcase a
                                        sponsor or logo.
                                    </Typography>
                                    <Typography variant="subtitle2">
                                        {
                                            '* Type: PNG, JPG. File Size < 1MB. Recommended Dimensions: 800x400, 1000x500'
                                        }
                                    </Typography>
                                    <Content mb={2}>
                                        {enableSponsor ? (
                                            <SponsorImagePicker value={image} onChange={setImage} />
                                        ) : (
                                            <>
                                                <FormControlLabel
                                                    disabled
                                                    control={<Switch checked={false} />}
                                                    label="Enable"
                                                />
                                                <Typography style={{ fontStyle: 'italic' }}>
                                                    *Speak with your FrontDoor+ rep to have this
                                                    feature enabled.
                                                </Typography>
                                            </>
                                        )}
                                    </Content>
                                </Section>
                            </Grid>

                            {attendeeRegistration && (
                                <Grid item xs={12}>
                                    <Section>
                                        <Header>Checkout Questions</Header>
                                        <Typography>
                                            Attendee checkout questions are enabled. Should this
                                            ticket collect attendee information?
                                        </Typography>
                                        <Content>
                                            <FormControl error={isAttendee.formProps.error}>
                                                <RadioGroup
                                                    {...isAttendee.formProps}
                                                    aria-label="attendee registration"
                                                >
                                                    <FormControlLabel
                                                        value="true"
                                                        control={<Radio />}
                                                        label="Yes, this is an attendee ticket"
                                                    />
                                                    <FormControlLabel
                                                        value="false"
                                                        control={<Radio />}
                                                        label="No, don't collect attendee information"
                                                    />
                                                </RadioGroup>
                                                <FormHelperText error={isAttendee.formProps.error}>
                                                    {isAttendee.formProps.helperText || ''}
                                                </FormHelperText>
                                            </FormControl>
                                        </Content>
                                    </Section>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Section>
                            <ReceiptPreview
                                title="Online Pricing"
                                hostId={hostId}
                                eventId={eventId}
                                price={
                                    enableSeatPricing &&
                                        seatPricing.categories[selectedCategoryIndex]?.enabled
                                        ? seatPricing.categories[selectedCategoryIndex]?.price ||
                                        '0'
                                        : price.value
                                }
                                name={
                                    enableSeatPricing &&
                                        seatPricing.categories[selectedCategoryIndex]?.enabled
                                        ? `${name.value} - ${seatPricing.categories[selectedCategoryIndex]?.name || ''}`
                                        : name.value
                                }
                            />
                            {enableSeatPricing && seatPricing.categories.some((c) => c.enabled) && (
                                <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
                                    {seatPricing.categories.map((category, index) =>
                                        category.enabled ? (
                                            <Button
                                                key={category.key}
                                                size="small"
                                                variant={
                                                    index === selectedCategoryIndex
                                                        ? 'contained'
                                                        : 'outlined'
                                                }
                                                onClick={() => setSelectedCategoryIndex(index)}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                {category.name}
                                            </Button>
                                        ) : null,
                                    )}
                                </Box>
                            )}
                        </Section>
                    </Grid>
                </Grid>
            )}

            {tab === 1 && (
                <>
                    <Box mb={4}>
                        <Alert severity="info">
                            The <Bold variant="inherit">FrontDoor+ | Point-of-Sale (POS)</Bold> app
                            is used to accept debit & credit payments as well as track cash. You can
                            customize the POS details below.
                        </Alert>
                    </Box>

                    <Grid container spacing={4}>
                        <Grid item xs={12} md={8}>
                            <Grid container spacing={4}>
                                <Grid item xs={12}>
                                    <Section>
                                        <Header>POS Ticket Visibility</Header>
                                        <Typography gutterBottom>
                                            Specify whether this ticket will be displayed and
                                            available for sale within the{' '}
                                            <Bold>FrontDoor+ | Point-of-Sale (POS)</Bold> app.
                                        </Typography>
                                        <Content>
                                            <RadioGroup
                                                {...posVisible.formProps}
                                                aria-label="POS Visibility"
                                            >
                                                <FormControlLabel
                                                    value="visible"
                                                    control={<Radio />}
                                                    label={
                                                        <Typography>
                                                            <Bold>Yes</Bold> - make this ticket
                                                            visible in the POS app for in-person
                                                            purchases
                                                        </Typography>
                                                    }
                                                />
                                                <FormControlLabel
                                                    value="hidden"
                                                    control={<Radio />}
                                                    label={
                                                        <Typography>
                                                            <Bold>No</Bold> - don't make this ticket
                                                            visible in the POS app for in-person
                                                            purchases
                                                        </Typography>
                                                    }
                                                />
                                            </RadioGroup>
                                        </Content>
                                    </Section>
                                </Grid>

                                <Grid item xs={12}>
                                    <Section>
                                        <Header>Nickname</Header>
                                        <Typography>
                                            Provide a shortened or alternate name for this ticket.
                                            This will be displayed in the{' '}
                                            <Bold>FrontDoor+ | Point-of-Sale (POS)</Bold> app during
                                            in-person transactions. By default, the online Ticket
                                            Name is used.
                                        </Typography>
                                        <Content>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        {...posName.formProps}
                                                        autoFocus={autoFocus}
                                                        label="Nickname"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Content>
                                    </Section>
                                </Grid>

                                <Grid item xs={12}>
                                    <Section>
                                        <Header>Price</Header>
                                        <Typography>
                                            Override the ticket price specifically for point-of-sale
                                            transactions. By default, the online price is utilized,
                                            but here you can adjust it as needed for in-person
                                            purchases.
                                        </Typography>
                                        <Content>
                                            <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        {...posPrice.formProps}
                                                        label="In-Person Price"
                                                        value={
                                                            posPrice.value === 0
                                                                ? 'Free'
                                                                : posPrice.value
                                                        }
                                                        disabled={posFree.checked}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    $
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid
                                                    item
                                                    xs={6}
                                                    style={{
                                                        display: 'flex',
                                                        alignContent: 'center',
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        control={<Switch {...posFree.formProps} />}
                                                        label="Free"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Content>
                                    </Section>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Section>
                                <ReceiptPreview
                                    pos
                                    title="In-Person Pricing"
                                    hostId={hostId}
                                    eventId={eventId}
                                    price={posPrice.value || price.value}
                                    name={name.value}
                                />
                            </Section>
                        </Grid>
                    </Grid>

                    <Box mt={4}>
                        <Alert severity="info">
                            Reach out to us if you'd like to learn more about our in-person payment
                            and cash tracking options.
                        </Alert>
                    </Box>
                </>
            )}

            <Grid container spacing={4}>
                <Grid item xs={12} md={8}>
                    <Box display="flex" justifyContent="flex-end" mb={4} mt={4}>
                        {editMode && (
                            <>
                                {deletable ? (
                                    <DeleteButton
                                        sx={{ marginRight: 'auto' }}
                                        onClick={onDelete}
                                        disabled={loading}
                                    >
                                        Delete
                                    </DeleteButton>
                                ) : (
                                    <Tooltip message="Tickets and add-ons cannot be deleted after being purchased on the store, or included in a bundle.">
                                        <span style={{ marginRight: 'auto' }}>
                                            <DeleteButton disabled>Delete</DeleteButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </>
                        )}
                        <CancelButton onClick={onCancel} disabled={loading}>
                            Cancel
                        </CancelButton>
                        <SubmitButton onClick={handleSubmit} disabled={loading}>
                            Save
                        </SubmitButton>
                    </Box>

                    {createdAtMsg && (
                        <Typography display="block" align="right" variant="caption">
                            {createdAtMsg}
                        </Typography>
                    )}

                    {updatedAtMsg && (
                        <Typography display="block" align="right" variant="caption">
                            {updatedAtMsg}
                        </Typography>
                    )}
                </Grid>
            </Grid>
        </Root>
    );
}

function SeatPricing(props) {
    const { seatPricing, setSeatPricing } = props;

    return (
        <>
            {seatPricing.error && (
                <Typography color="error" gutterBottom>
                    {seatPricing.error}
                </Typography>
            )}
            <Grid container spacing={2}>
                {seatPricing.categories.map((category, index) => (
                    <Grid item xs={12} key={category.key}>
                        <Box display="flex" alignItems="center" justifyContent={'space-between'}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={category.enabled}
                                        onChange={(e) => {
                                            const updatedCategories = [...seatPricing.categories];
                                            updatedCategories[index].enabled = e.target.checked;
                                            updatedCategories[index].error = false;
                                            updatedCategories[index].price = '';
                                            setSeatPricing((sp) => ({
                                                ...sp,
                                                categories: updatedCategories,
                                                error: null,
                                            }));
                                        }}
                                    />
                                }
                                label={category.name}
                            />
                            <TextField
                                value={category.price}
                                error={category.error}
                                disabled={!category.enabled}
                                size="small"
                                helperText={category.error ? category.helperText : ''}
                                onChange={(e) => {
                                    const updatedCategories = [...seatPricing.categories];
                                    updatedCategories[index].price = formatCurrencyInput(
                                        e.target.value,
                                    );
                                    updatedCategories[index].error = false;
                                    setSeatPricing((sp) => ({
                                        ...sp,
                                        categories: updatedCategories,
                                        error: null,
                                    }));
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">$</InputAdornment>
                                    ),
                                }}
                                style={{
                                    marginLeft: '1rem',
                                    maxWidth: '110px',
                                    opacity: category.enabled ? 1 : 0.4,
                                }}
                            />
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </>
    );
}
function DateSummary(props) {
    const { startDate, startTime, endDate, endTime } = props;

    if (
        moment(startDate).isValid() === false ||
        moment(endDate).isValid() === false ||
        moment(startTime).isValid() === false ||
        moment(endTime).isValid() === false
    ) {
        return null;
    }

    const start = getMomentFromDateAndTime(startDate, startTime);
    const end = getMomentFromDateAndTime(endDate, endTime);
    const text = `${start.format('dddd, MMM Do @ h:mma')} and ${end.format('dddd, MMM Do @ h:mma')}`;

    return (
        <Box my={2}>
            <DateSummaryText>This ticket can be purchased between:</DateSummaryText>
            {start > end ? (
                <DateSummaryTextError>{text}</DateSummaryTextError>
            ) : (
                <DateSummaryTextBold>{text}</DateSummaryTextBold>
            )}
        </Box>
    );
}

function ScanDateSummary(props) {
    const { startDate, startTime, endDate, endTime, multiCheckins, unlimitedCheckins } = props;

    if (
        moment(startDate).isValid() === false ||
        moment(endDate).isValid() === false ||
        moment(startTime).isValid() === false ||
        moment(endTime).isValid() === false
    ) {
        return null;
    }

    const start = getMomentFromDateAndTime(startDate, startTime);
    const end = getMomentFromDateAndTime(endDate, endTime);

    const singleDay = start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD');

    const text = singleDay
        ? `${start.format('dddd MMM Do,')} ${start.format('h:mma')} to ${end.format('h:mma')}`
        : `${start.format('dddd, MMM Do @ h:mma')} and ${end.format('dddd, MMM Do @ h:mma')}`;

    return (
        <Box my={2}>
            <DateSummaryText display="inline">This ticket can be used </DateSummaryText>
            {unlimitedCheckins ? (
                <>
                    <DateSummaryTextBold display="inline">UNLIMITED TIMES </DateSummaryTextBold>
                    <DateSummaryText display="inline">between:</DateSummaryText>
                </>
            ) : start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD') ? (
                <>
                    <DateSummaryTextBold display="inline">ONCE </DateSummaryTextBold>
                    <DateSummaryText display="inline">on:</DateSummaryText>
                </>
            ) : (
                <>
                    <DateSummaryTextBold display="inline">
                        {multiCheckins ? 'ONCE PER DAY' : 'ONCE'}{' '}
                    </DateSummaryTextBold>
                    <DateSummaryText display="inline">between:</DateSummaryText>
                </>
            )}

            {start > end ? (
                <DateSummaryTextError>{text}</DateSummaryTextError>
            ) : (
                <DateSummaryTextBold>{text}</DateSummaryTextBold>
            )}
        </Box>
    );
}

function LabelModal(props) {
    const { labelColors, open, onClose, onSave } = props;

    const message = useTextInput({ defaultValue: props.message || '', maxLength: 15 });
    const [selectedColor, setSelectedColor] = useState(props.color || null);

    const handleSave = () => onSave(message.value.toUpperCase(), selectedColor);
    const handleDelete = () => onSave(null, '');

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Create a Label</DialogTitle>
            <DialogContent>
                <TextField {...message.formProps} label="Label Message" />
                <Box mt={4}>
                    <Typography>Select a Color:</Typography>
                </Box>
                <Box display="flex" flexWrap="wrap" mt={2}>
                    {labelColors.map((color) => (
                        <ColorSquare
                            key={color.color_id}
                            color={'#' + color.color_hex}
                            selected={color.color_id === selectedColor}
                            onClick={() => setSelectedColor(color.color_id)}
                        />
                    ))}
                </Box>
            </DialogContent>
            <DialogActions>
                <DeleteButton sx={{ marginRight: 'auto' }} onClick={handleDelete}>
                    Delete
                </DeleteButton>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton onClick={handleSave} disabled={!message || !selectedColor}>
                    Save
                </SubmitButton>
            </DialogActions>
        </Dialog>
    );
}

const ColorSquare = styled(Box)(({ color, selected, theme }) => ({
    width: 40,
    height: 40,
    backgroundColor: color,
    margin: theme.spacing(1),
    border: selected
        ? `3px solid ${theme.palette.common.black}`
        : `3px solid ${theme.palette.grey[200]}`,
    cursor: 'pointer',
    borderRadius: 10,
}));

const LabelPreview = styled(Box)(({ color, theme }) => ({
    height: 60,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: color ? '#' + color : undefined,
    border: color ? '1px solid grey' : '1px dashed grey',
    borderRadius: 4,
    padding: 2,
    cursor: 'pointer',
}));

const LabelMessagePreview = styled(Typography)(({ labelColor, theme }) => ({
    fontWeight: 'bold',
    fontSize: 24,
    color: theme.palette.getContrastText(
        labelColor ? '#' + labelColor : theme.palette.common.white,
    ),
}));

const TabItem = styled(Tab)(({ theme }) => ({
    '&.Mui-selected': {
        fontWeight: 'bold',
    },
}));

const Root = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4),
        maxWidth: theme.breakpoints.values.lg,
        marginRight: 'auto',
        marginLeft: 'auto',
    },
}));

const TabContainerWrapper = styled(Box)(({ theme }) => ({
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: theme.spacing(4),
}));

const TabContainer = styled(Box)(({ theme }) => ({
    width: 400,
    backgroundColor: 'white',
}));

const SwitchSubHeader = styled(Typography)(({ theme }) => ({
    marginLeft: 48,
    marginTop: -6,
    marginBottom: 8,
}));
SwitchSubHeader.defaultProps = { variant: 'caption' };

const SwitchTextField = styled(TextField)(({ theme }) => ({
    marginLeft: 48,
    maxWidth: 400,
}));
SwitchTextField.defaultProps = { size: 'small' };

const AccessCodeContainer = styled(Box)(({ theme }) => ({
    marginLeft: 32,
    display: 'flex',
    flexDirection: 'column',
}));

const AccessCodeInput = styled(TextField)(({ theme }) => ({
    maxWidth: 400,
    marginTop: theme.spacing(1),
}));
AccessCodeInput.defaultProps = { size: 'small' };

const DateSummaryText = styled(Typography)(({ theme }) => ({
    color: theme.palette.info.dark,
}));

const DateSummaryTextBold = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    color: theme.palette.info.dark,
}));

const DateSummaryTextError = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    color: theme.palette.error.main,
}));

const Bold = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    display: 'inline',
}));
